import React from 'react';
import { Alert, Box, ProgressBar, Text } from '@razorpay/blade/components';
import { GetComplianceActionsSchemaContract } from 'schemas/GetComplianceActionsSchema';
import { actionStatusesMap } from 'components/RunPayroll/ComplianceActions/data';
import { useRunPayrollDataContext } from 'components/RunPayroll/contexts/RunPayrollDataContext';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';

export const PayrollProgress = ({
  action,
  hideAlert,
}: {
  action: GetComplianceActionsSchemaContract['actions']['pay-pt'][string];
  hideAlert?: boolean;
}) => {
  const payrollMonth = useRunPayrollDataContext()?.payrollMonth;
  const coveredEmployees = action.meta.pt_payment_employee_count || 0;
  const skippedEmployees = action.meta.pt_skipped_employees || 0;

  const pendingEmployees = (action.meta.pt_pending_employees || 0) + skippedEmployees;
  const payableEmployees = coveredEmployees + pendingEmployees;
  const progress = payableEmployees ? (coveredEmployees / payableEmployees) * 100 : 0;

  const isActionable =
    action.status === actionStatusesMap.failed || action.status === actionStatusesMap.notInitiated;

  const payrollMonthLabel = getFormattedDateValue({
    date: payrollMonth,
    formatString: dateFormats.shortMonthYear,
    filler: 'this month',
  });

  const alertMessage =
    isActionable && pendingEmployees > 0
      ? `${pendingEmployees} of ${payableEmployees} employees salary payment is still pending. You can pay KA PT for the ${coveredEmployees} covered employees now. After this payment, XPayroll will not process any further KA PT payments for ${payrollMonthLabel} from the dashboard, so PT for the remaining ${pendingEmployees} must be paid outside XPayroll. To cover everyone in one payment, process payroll for all pending employees first.`
      : undefined;

  return (
    <>
      <Box paddingX="spacing.5" paddingBottom="spacing.5">
        <ProgressBar variant="linear" value={progress} showPercentage label="KA PT Coverage" />
        <Box
          marginTop="spacing.3"
          display="flex"
          gap="spacing.3"
          justifyContent="space-between"
          alignItems="center">
          <Box display="flex" flexDirection="column" gap="spacing.1">
            <Text size="small" color="surface.text.gray.muted">
              Employees Covered
            </Text>
            <Text size="large" weight="semibold">
              {coveredEmployees}
            </Text>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap="spacing.1"
            flexGrow={1}
            justifyContent="flex-end"
            textAlign="right">
            <Text size="small" color="interactive.text.notice.normal">
              Employees Pending
            </Text>
            <Text size="large" weight="semibold" color="interactive.text.notice.normal">
              {pendingEmployees}
            </Text>
          </Box>
        </Box>
      </Box>
      {!hideAlert && alertMessage ? (
        <Alert
          testID="payroll-progress-alert"
          color="notice"
          isDismissible={false}
          isFullWidth={true}
          description={alertMessage}
        />
      ) : null}
    </>
  );
};
